// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useIntl} from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { IAssetDto } from '../Asset.model'
import { PreviewCard } from '../../../common/component/PreviewCard'
import { SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { parseLocalDate } from '../../../util/Locale'
import { AssetMetadataViewer } from './AssetMetadataViewer'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { ContractEmbedered } from '../../contract/components/ContractEmbedered'

interface Props {
  asset?: IAssetDto
  loading: boolean
  setRowSelected: (rowId?: string) => void
}

const AssetPreview: FC<Props> = ({asset, loading, setRowSelected}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/assets/${asset?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/assets/${asset?.id}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.REPORT'}),
      icon: 'bi-speedometer',
      action: () => navigate(`/reports/new?assetId=${asset?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.TICKET'}),
      icon: 'bi-wrench-adjustable-circle',
      action: () => navigate(`/tickets/new?assetId=${asset?.id}`),
    },
  ]

  return (
    <PreviewCard
      loading={loading}
      title={asset?.displayName}
      subTitle={asset?.sequence}
      pathOnClickHandler={`/assets/${asset?.id}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        asset && (
          <div>
            <div className='row mt-10'>
              <div className='px-5'>
                <div className='row mb-5'>
                  <ClientEmbedered client={asset.client} />

                  {asset && asset.contract && <ContractEmbedered contract={asset.contract} />}
                </div>
              </div>
            </div>

            <div className='row'>
              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={parseLocalDate(asset.updatedAt, intl.formatMessage({id: 'I18N.LOCALE'}))}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${asset.sequence}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/assets/${asset.id}`}>
                          {asset.displayName}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>

                  {/* <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.GUI'})}
                    value={asset.gui}
                  ></CustomInputRenderer> */}

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSET_FAMILY'})}
                    value={asset.family}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSET_AREA'})}
                    value={asset.area}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSET_BRAND'})}
                    value={asset.brand}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSET_MODEL'})}
                    value={asset.model}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSET_SERIAL'})}
                    value={asset.serial}
                  ></CustomInputRenderer>
                </div>
              </div>

              {asset.metadata && asset.metadata.items && (
                <>
                  <SubSectionHeader
                    title={intl.formatMessage({id: 'UTIL.DETAILS'})}
                    separator={false}
                  />

                  <div className='px-5'>
                    <div className='row mb-5'>
                      <AssetMetadataViewer
                        keys={[]}
                        collection={asset.metadata.items}
                        path={`metadata.items`}
                        disabled={true}
                      />
                    </div>
                  </div>
                </>
              )}

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.LOCATION'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                    value={
                      asset.countryCode
                        ? intl.formatMessage({
                            id: `DROPDOWN.COUNTRY.${asset.countryCode}`,
                          })
                        : ''
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                    value={asset.address}
                  ></CustomInputRenderer>
                </div>
              </div>

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.METRICS'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                    value={`${asset.totalReport || 0}`}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}

export {AssetPreview}
